import React from 'react'
import { ControlledModal, ModalTitle } from './../layouts/components/modal'
import { TextInput } from '../components/form/textInput'
import { Button } from './../components/simple/button'
import { Select } from './../components/form/select'
import { useTranslation } from './translation'
import { useAppContext } from './context'
import { LabeledCheckbox } from '../components/form/checkbox'

type BasePromptPopup = {
  type: 'prompt'
  title: React.ReactNode
  defaultValue?: string
  confirm?: React.ReactNode
  cancel?: React.ReactNode
  message?: React.ReactNode
  info?: string
}

type PromptPopupWithCheckbox = {
  callback: (result: [string, boolean] | undefined) => void
  checkbox: {
    label: React.ReactNode
    defaultChecked?: boolean
  }
} & BasePromptPopup

type PromptPopupWithoutCheckbox = {
  callback: (result: string | undefined) => void
  checkbox?: undefined
} & BasePromptPopup

type PromptPopup = PromptPopupWithCheckbox | PromptPopupWithoutCheckbox

type ConfirmPopup = {
  type: 'confirm'
  title?: React.ReactNode
  message?: React.ReactNode
  yesText?: React.ReactNode
  noText?: React.ReactNode
  cancel?: React.ReactNode
  closeable?: boolean
  callback: (result: boolean | undefined) => void
  info?: string
}

type SelectPopup = {
  type: 'select'
  title: React.ReactNode
  options: { value: string; name: string }[]
  value?: string
  callback: (result: { value: string; name: string } | undefined) => void
  yesText?: React.ReactNode
  cancel?: React.ReactNode
  info?: string
}

type ShowPrompt = {
  (p: Omit<PromptPopupWithCheckbox, 'callback' | 'type'>): Promise<[string, boolean] | undefined>
  (p: Omit<PromptPopupWithoutCheckbox, 'callback' | 'type'>): Promise<string | undefined>
}
type ShowConfirm = (c: Omit<ConfirmPopup, 'callback' | 'type'>) => Promise<boolean | undefined>
type ShowSelect = (c: Omit<SelectPopup, 'callback' | 'type'>) => Promise<{ value: string; name: string } | undefined>

type Popup = PromptPopup | ConfirmPopup | SelectPopup

type PopupsContext = {
  prompt: ShowPrompt
  confirm: ShowConfirm
  select: ShowSelect
  simpleConfirm: (title: React.ReactNode, message?: React.ReactNode) => Promise<boolean | undefined>
  simplePopup: (title: React.ReactNode) => Promise<boolean | undefined>
}
export const popupsContext = React.createContext<PopupsContext>({} as PopupsContext)

/**
 * A hook to get the Popups context, allowing you to show popups.
 * In most cases, you can just use the Popups object directly, since we usually do not need to react to the popup state.
 */
export const usePopups = () => {
  return React.useContext(popupsContext)
}

/**
 * Popups is a static object that contains the list of all popups and helper functions to show them.
 * You can use it directly if you don't need to react to the popups state.
 * If you need to react to the popups state, you should use usePopups() instead.
 */
export const Popups: PopupsContext = {} as PopupsContext

export const PopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [popup, setPopup] = React.useState<Popup | undefined>(undefined)

  const { tr } = useTranslation()

  const { shouldUnmountPage } = useAppContext()
  // clear all popups when the page is unmounted
  React.useEffect(() => {
    if (shouldUnmountPage) setPopup(undefined)
  }, [shouldUnmountPage])

  const prompt = React.useCallback((p: any): any => {
    if (p.checkbox) {
      return new Promise<[string, boolean] | undefined>((resolve) => {
        setPopup({
          ...p,
          type: 'prompt',
          callback: (v: [string, boolean] | undefined) => {
            setPopup(undefined)
            resolve(v)
          },
        })
      })
    } else {
      return new Promise<string | undefined>((resolve) => {
        setPopup({
          ...p,
          type: 'prompt',
          callback: (v: string | undefined) => {
            setPopup(undefined)
            resolve(v)
          },
        })
      })
    }
  }, [])

  const confirm = React.useCallback(
    (c: Omit<ConfirmPopup, 'callback' | 'type'>) =>
      new Promise<boolean | undefined>((resolve) => {
        setPopup({
          ...c,
          type: 'confirm',
          callback: (v) => {
            setPopup(undefined)
            resolve(v)
          },
        })
      }),
    []
  )

  const select = React.useCallback(
    (c: Omit<SelectPopup, 'callback' | 'type'>) =>
      new Promise<{ value: string; name: string } | undefined>((resolve) => {
        setPopup({
          ...c,
          type: 'select',
          callback: (v) => {
            setPopup(undefined)
            resolve(v)
          },
        })
      }),
    []
  )

  const simpleConfirm = React.useCallback(
    (title: React.ReactNode, message?: React.ReactNode) =>
      confirm({
        title,
        message,
        yesText: tr('Yes'),
        noText: tr('No'),
      }),
    [confirm, tr]
  )

  const simplePopup = React.useCallback(
    (title: React.ReactNode) =>
      confirm({
        title,
        yesText: tr('OK'),
      }),
    [confirm, tr]
  )

  React.useEffect(() => {
    Popups.prompt = prompt
    Popups.confirm = confirm
    Popups.select = select
    Popups.simpleConfirm = simpleConfirm
    Popups.simplePopup = simplePopup
  }, [prompt, confirm, simpleConfirm, simplePopup])

  return (
    <popupsContext.Provider value={{ prompt, confirm, simpleConfirm, simplePopup, select }}>
      {children}
      {popup?.type === 'prompt' ? (
        <PromptPopupElem {...popup} />
      ) : popup?.type === 'confirm' ? (
        <ConfirmPopupElem {...popup} />
      ) : popup?.type === 'select' ? (
        <SelectPopupElem {...popup} />
      ) : undefined}
    </popupsContext.Provider>
  )
}

const PromptPopupElem: React.FC<PromptPopup> = ({
  title,
  defaultValue,
  confirm,
  cancel,
  message,
  callback,
  info,
  checkbox,
}) => {
  const [value, setValue] = React.useState(defaultValue || '')
  const [invalid, setInvalid] = React.useState(false)
  const [isChecked, setIsChecked] = React.useState(checkbox?.defaultChecked || false)

  const onCancel = () => {
    callback(undefined)
  }

  const onConfirm = () => {
    if (!value || value.trim() === '') {
      setInvalid(true)
      return
    }
    if (checkbox) {
      callback([value, isChecked])
    } else {
      callback(value)
    }
  }

  return (
    <ControlledModal
      open={true}
      closeable={cancel !== undefined}
      onOpenChange={onCancel}
      size="sm"
      centered
      info={info}
      title={title}
      overlayClassName="!z-[3000]"
    >
      <TextInput value={value} onChange={setValue} invalid={invalid} />
      {message && <div className="mt-1 text-sm text-gray-500">{message}</div>}
      {checkbox && (
        <LabeledCheckbox className="mt-2" checked={isChecked} onChecked={setIsChecked} label={checkbox.label} />
      )}
      <div className="mt-4 flex justify-end gap-2">
        {cancel && <Button onClick={onCancel}>{cancel}</Button>}
        <Button onClick={onConfirm} logo>
          {confirm}
        </Button>
      </div>
    </ControlledModal>
  )
}

const ConfirmPopupElem: React.FC<ConfirmPopup> = ({
  title,
  yesText,
  noText,
  cancel,
  callback,
  info,
  message,
  closeable,
}) => {
  const onCancel = () => {
    callback(undefined)
  }

  return (
    <ControlledModal
      open={true}
      closeable={cancel !== undefined || closeable}
      onOpenChange={onCancel}
      size="sm"
      centered
      info={info}
      overlayClassName="!z-[3000]"
    >
      <div className="flex flex-col gap-2">
        {title ? <ModalTitle className="mr-4">{title}</ModalTitle> : <ModalTitle hidden>{message}</ModalTitle>}
        {message && <div className="">{message}</div>}
        <div className="hide-if-empty mt-4 flex justify-end gap-2">
          {cancel && <Button onClick={onCancel}>{cancel}</Button>}
          {noText !== undefined && <Button onClick={() => callback(false)}>{noText}</Button>}
          {yesText && (
            <Button onClick={() => callback(true)} logo>
              {yesText}
            </Button>
          )}
        </div>
      </div>
    </ControlledModal>
  )
}

const SelectPopupElem: React.FC<SelectPopup> = ({
  title,
  options,
  value: defaultValue,
  callback,
  info,
  yesText,
  cancel,
}) => {
  const [value, setValue] = React.useState(
    (defaultValue && options.find((o) => o.value === defaultValue)) || options[0]
  )
  const onCancel = () => callback(undefined)
  const onConfirm = () => callback(value)

  return (
    <ControlledModal
      open={true}
      closeable={cancel !== undefined}
      onOpenChange={onCancel}
      size="sm"
      centered
      info={info}
      title={title}
      noAutoFocus={!!info} // we dont want to auto focus the info
      overlayClassName="!z-[3000]"
    >
      <div className="flex flex-col gap-2">
        <Select options={options} value={value} onChange={setValue} />
        <div className="mt-4 flex justify-end gap-2">
          {cancel && <Button onClick={onCancel}>{cancel}</Button>}
          <Button onClick={onConfirm} logo>
            {yesText}
          </Button>
        </div>
      </div>
    </ControlledModal>
  )
}
